import type { AdminRuleVersionGetResponse, Clause, PatternBased, RuleBased, RuleSet, RuleVersionSummary } from '@kkp-fraud/shared';

/**
 * Four releases from before the real history begins, so the release log and a rule's
 * version history have a story to show. Each is the real document walked BACKWARDS through
 * the changes its successor's comment describes — 0.9.3 is the oldest real document itself,
 * 0.9.2 is that with PB-01's threshold back at 4, and so on — so every "changed" marker and
 * every diff on screen is a genuine difference between two documents.
 *
 * They exist only in the console: activating one would 404 upstream, so the screens hide
 * Rollback / Restore for them (`isDemoVersion`).
 */

export const DEMO_ACTORS = {
  analyst: 'fraud.analyst@kkp.co.th',
  lead: 'fraud.lead@kkp.co.th',
} as const;

interface DemoVersionSpec {
  version: string;
  actor: string;
  comment: string;
  /** Days before the oldest real release. */
  daysBefore: number;
  /** Bangkok clock time of the release. */
  bkkHour: number;
  bkkMinute: number;
  /** Turns THIS version's document into its predecessor's (undoes what the comment says). */
  undo: (doc: RuleSet) => void;
}

const DAY = 86_400_000;
const BKK_OFFSET_MS = 7 * 3600_000;

function rb(doc: RuleSet, id: string): RuleBased | undefined {
  return doc.ruleBased.find((r) => r.id === id);
}
function pbRule(doc: RuleSet, id: string): PatternBased | undefined {
  return doc.patternBased.find((r) => r.id === id);
}

/** Edit a clause on a rule-based rule and on its first action row, which must mirror it. */
function editClause(rule: RuleBased | undefined, field: string, op: string, edit: (c: Clause) => void): void {
  if (!rule) return;
  const lists = [rule.clauses, rule.branches?.[0]?.clauses].filter((l): l is Clause[] => Array.isArray(l));
  for (const list of lists) for (const c of list) if (c.field === field && c.op === op) edit(c);
}

function setThreshold(rule: PatternBased | undefined, value: number): void {
  if (!rule) return;
  rule.threshold = value;
  // Row 1's first measure mirrors the rule level; writing only the legacy `threshold` would
  // let the two drift and fail the mirror check on a branched rule.
  const measure = rule.branches?.[0]?.measures?.[0];
  if (measure) measure.threshold = value;
}

// 2a expressions
/**
 * The user's example as an advanced-condition rule (docs/design/rule-expressions.md §3.3, in
 * the 24 h form Wave 2a can count): approvals over 50,000 plus approvals of at least 30,000 at
 * SHOP_A, 10 or more in 24 h → step-up. It lives only in the demo history (0.9.0 has it, 0.9.1
 * removed it), never in the live document — the demo writes nothing, so a demo rule in the
 * live draft would be published by the next release.
 */
export const DEMO_EXPRESSION_RULE: PatternBased = {
  id: 'PB-06',
  name: 'Large + SHOP_A approvals together ≥ 10 in 24 h',
  priority: 'P2',
  action: 'STEP_UP',
  points: 50,
  enabled: true,
  thresholdConfirmed: false,
  scope: 'card_id',
  event: 'approved',
  aggregate: 'count',
  threshold: 1,
  windowSeconds: 86400,
  filters: [{ field: 'amount_thb', op: 'gt', value: 50000 }],
  branches: [
    {
      action: 'STEP_UP',
      measures: [
        { name: 'big', scope: 'card_id', event: 'approved', aggregate: 'count', windowSeconds: 86400, filters: [{ field: 'amount_thb', op: 'gt', value: 50000 }] },
        {
          name: 'shopA',
          scope: 'card_id',
          event: 'approved',
          aggregate: 'count',
          windowSeconds: 86400,
          filters: [
            { field: 'merchant_id', op: 'eq', value: 'SHOP_A' },
            { logic: 'AND', field: 'amount_thb', op: 'gte', value: 30000 },
          ],
        },
      ],
      conditions: [{ left: { op: 'add', args: [{ m: 'big' }, { m: 'shopA' }] }, cmp: 'gte', right: 10 }],
    },
  ],
};

/** Newest first. */
export const DEMO_VERSION_SPECS: readonly DemoVersionSpec[] = [
  {
    version: '0.9.3-poc',
    actor: DEMO_ACTORS.lead,
    comment: 'PB-01 threshold 4 → 3 after workshop',
    daysBefore: 1,
    bkkHour: 15,
    bkkMinute: 42,
    undo: (doc) => setThreshold(pbRule(doc, 'PB-01'), 4),
  },
  {
    version: '0.9.2-poc',
    actor: DEMO_ACTORS.lead,
    comment: 'RB-02 add HKD',
    daysBefore: 5,
    bkkHour: 11,
    bkkMinute: 8,
    undo: (doc) =>
      editClause(rb(doc, 'RB-02'), 'currency_code_num', 'in', (c) => {
        if (Array.isArray(c.value)) c.value = c.value.filter((v) => v !== 344);
      }),
  },
  {
    version: '0.9.1-poc',
    actor: DEMO_ACTORS.lead,
    comment: 'RB-03 amount floor 30,000 → 20,000 THB; RB-05 switched on; PB-06 (advanced condition) removed until the 7-day window (Phase C)',
    daysBefore: 10,
    bkkHour: 16,
    bkkMinute: 20,
    undo: (doc) => {
      // 2a expressions — 0.9.0 still had the user's example
      if (!pbRule(doc, DEMO_EXPRESSION_RULE.id)) doc.patternBased.push(structuredClone(DEMO_EXPRESSION_RULE));
      editClause(rb(doc, 'RB-03'), 'amount_thb', 'gt', (c) => {
        c.value = 30000;
      });
      const rb05 = rb(doc, 'RB-05');
      if (rb05) rb05.enabled = false;
    },
  },
  {
    version: '0.9.0-poc',
    actor: DEMO_ACTORS.analyst,
    comment: 'Baseline from the fraud workshop — 5 rule-based, 6 pattern-based',
    daysBefore: 16,
    bkkHour: 10,
    bkkMinute: 35,
    undo: () => {},
  },
];

const DEMO_VERSION_NAMES = new Set(DEMO_VERSION_SPECS.map((s) => s.version));

/** True for a version that exists only in the console's demo history. */
export function isDemoVersionName(version: string): boolean {
  return DEMO_VERSION_NAMES.has(version);
}

export interface DemoVersion {
  summary: RuleVersionSummary;
  detail: AdminRuleVersionGetResponse;
}

/** Bangkok wall-clock time `daysBefore` days before `anchor`, always strictly before it. */
function releaseTime(anchor: number, spec: DemoVersionSpec): number {
  const day = Math.floor((anchor - spec.daysBefore * DAY - 3 * 3600_000 + BKK_OFFSET_MS) / DAY) * DAY - BKK_OFFSET_MS;
  return day + (spec.bkkHour * 60 + spec.bkkMinute) * 60_000;
}

/**
 * The demo releases, newest first, dated before `oldestRealUtc` (or `now` when there is no
 * real history). `base` is the OLDEST real document — the one 0.9.3 turned into.
 */
export function demoVersions(base: RuleSet, oldestRealUtc: string | null, now = Date.now()): DemoVersion[] {
  const oldest = oldestRealUtc ? Date.parse(oldestRealUtc) : NaN;
  const anchor = Number.isFinite(oldest) ? Math.min(oldest, now) : now;
  let doc: RuleSet = structuredClone(base);
  const out: DemoVersion[] = [];
  for (const spec of DEMO_VERSION_SPECS) {
    const savedUtc = new Date(releaseTime(anchor, spec)).toISOString();
    const ruleSet: RuleSet = { ...structuredClone(doc), version: spec.version, updatedUtc: savedUtc };
    out.push({
      summary: {
        version: spec.version,
        savedUtc,
        actor: spec.actor,
        comment: spec.comment,
        ruleBasedEnabled: ruleSet.ruleBased.filter((r) => r.enabled).length,
        patternBasedEnabled: ruleSet.patternBased.filter((r) => r.enabled).length,
        isActive: false,
      },
      detail: { version: spec.version, savedUtc, actor: spec.actor, comment: spec.comment, ruleSet },
    });
    doc = structuredClone(doc);
    spec.undo(doc);
  }
  return out;
}

/** Real history first (newest first), then the demo releases — skipping any name the real list already has. */
export function mergeVersions(real: readonly RuleVersionSummary[], demo: readonly DemoVersion[]): RuleVersionSummary[] {
  const names = new Set(real.map((v) => v.version));
  return [...real, ...demo.filter((d) => !names.has(d.summary.version)).map((d) => d.summary)];
}
